import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface PresignedUrlResponse {
  success: boolean;
  message: string;
  data: PresignedUrlData;
  errors: string[];
}

export interface PresignedUrlData {
  url: string;
}

interface UrlCache {
  region: string;
  bucket: string;
  key: string;
  response: any;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class PresignedUrlService {
  downloadUrlCache: UrlCache[] = [];

  constructor(private _httpClient: HttpClient, private _auth: AuthService) {}

  async generateUploadUrl(
    region: string = environment.s3.region,
    bucket: string = environment.s3.bucket,
    key: string
  ): Promise<PresignedUrlResponse> {
    let headers = await this._auth.generateRequestHeaders();
    let options = {
      headers,
    };
    const body = {
      region: region,
      bucket: bucket,
      key: key,
    };
    return this._httpClient
      .post<PresignedUrlResponse>(
        `${environment.api.baseUrl}/presigned-url/generate-upload`,
        body,
        options
      )
      .toPromise();
  }

  private checkCache(region: string, bucket: string, key: string): any {
    if (region === undefined || bucket === undefined || key === undefined)
      return undefined;
    const cacheHit = this.downloadUrlCache
      .filter(
        (cache) =>
          cache.key === key &&
          cache.bucket === bucket &&
          cache.region == region &&
          cache.timestamp > new Date().getMilliseconds()
      )
      .pop();
    return cacheHit?.response;
  }

  private addToCache(
    region: string,
    bucket: string,
    key: string,
    response: any
  ) {
    const cachedItem: UrlCache = {
      region: region,
      bucket: bucket,
      key: key,
      response: response,
      timestamp: new Date().getMilliseconds(),
    };
    if (response.success === true) this.downloadUrlCache.push(cachedItem);
  }

  async generateDownloadUrl(
    region: string = environment.s3.region,
    bucket: string = environment.s3.bucket,
    key: string
  ): Promise<PresignedUrlResponse> {
    const cacheHit = this.checkCache(region, bucket, key);
    if (cacheHit !== undefined) return cacheHit;
    let headers = await this._auth.generateRequestHeaders();
    let options = {
      headers,
    };
    const body = {
      region: region,
      bucket: bucket,
      key: key,
    };
    const result = await this._httpClient
      .post<PresignedUrlResponse>(
        `${environment.api.baseUrl}/presigned-url/generate-download`,
        body,
        options
      )
      .toPromise();
    this.addToCache(region, bucket, key, result);
    return result;
  }

  generateGuidV4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (char: string) {
        const random = (Math.random() * 16) | 0;
        const v = char === 'x' ? random : (random & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  uploadFileToS3(signedUrl: string, file: File): Promise<HttpEvent<any>> {
    const req = new HttpRequest('PUT', signedUrl, file, {
      headers: new HttpHeaders({
        'Content-Type': file?.type ?? 'binary/octet-stream',
      }),
    });

    return this._httpClient.request(req).toPromise();
  }
}
